import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useIntl } from '@grandvisionhq/state';
import { useAddViewportHeightCssVariable } from '@grandvisionhq/utils';
import { bookAppointmentWhatStepScreenEvent } from '../../data-layer/actions.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import AppointmentTypes from './AppointmentTypes.js';
import useGetAgeGroupsSelectorAndSelectedAgeGroup from './useGetAgeGroupsSelectorAndSelectedAgeGroup.js';
import useGetAppointmentTypesForSelectedAgeGroup from './useGetAppointmentTypesForSelectedAgeGroup.js';
import useGetAvailableAgeGroups from './useGetAvailableAgeGroups.js';
import useBookAppointmentStore from '../../pages/BookAppointment/hooks/api/use-book-appointment-store.js';
import { useAppointmentTypes } from '../../pages/BookAppointment/hooks/use-appointment-types.js';
import { fromArrayToString } from '../../utils/from-array-to-string.js';

const AppointmentTypesContainer = ()=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [{ setStore , setType  }, { store  }] = useAppointmentContext();
    const [getBookAppointmentStore, getBookAppointmentStoreQuery] = useBookAppointmentStore();
    useAddViewportHeightCssVariable();
    const { sortedAppointmentTypes , appointmentTypeIcons , appointmentTypeToolTips , appointmentGlobalLabels ,  } = useAppointmentTypes();
    const [activeToolTip, setActiveToolTip] = React.useState(null);
    const labels = React.useMemo(()=>({
            heading: getLabel('appointments.component.appointmentTypes.heading'),
            description: getFormattedLabel('appointments.component.appointmentTypes.description', {
                hideLabelKey: true,
                parseLink: true,
                openLinksInNewTab: true
            })
        })
    , [
        getLabel,
        getFormattedLabel
    ]);
    const onClick = React.useCallback((type)=>{
        /* istanbul ignore if */ if (!store) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        setType(type);
        const typeId = type.id;
        const storeId = `${store.code}`;
        const { search , userLocation , linkedAppointmentTypeId  } = queryParams;
        goTo('when', {
            typeId,
            storeId,
            search,
            userLocation,
            linkedAppointmentTypeId
        });
    }, [
        queryParams,
        setType,
        goTo,
        store
    ]);
    const onClickToolTip = React.useCallback((toolTip)=>{
        setActiveToolTip(toolTip);
    }, []);
    const onCloseToolTip = React.useCallback(()=>{
        setActiveToolTip(null);
    }, []);
    React.useEffect(()=>{
        /* istanbul ignore if */ if (!store) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        const { storeId  } = queryParams;
        const { appointmentTypes  } = store;
        /** Need to check for the appointment types existence to be sure that
     * we are going to send correct store data to the event */ if (Number(storeId) === store.code && appointmentTypes.length) {
            const dataLayerEvent = bookAppointmentWhatStepScreenEvent({
                store: {
                    code: store.code,
                    streetName: store.streetName,
                    town: store.town,
                    name: store.name,
                    postalCode: store.postalCode,
                    streetNumber: store.streetNumber,
                    additionalStreetInfo: store.additionalStreetInfo
                }
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        queryParams,
        store
    ]);
    React.useEffect(()=>{
        const { storeId: storeIdFromUrl  } = queryParams;
        const storeId = fromArrayToString(storeIdFromUrl);
        /** If no store in the context but has a storeId in the URL refetch store data */ if (!store) {
            if (storeId) {
                getBookAppointmentStore(Number(storeId));
            }
            return;
        }
        /** Refetch store data with parameters from url
     *  if has different storeId in url and in the context */ if (storeId && storeId !== `${store.code}`) {
            getBookAppointmentStore(Number(storeId));
            return;
        }
        const { code , appointmentTypes  } = store;
        if (appointmentTypes === null || appointmentTypes === void 0 ? void 0 : appointmentTypes.length) {
            return;
        }
        // Only request the store data when no appointmentTypes are found.
        getBookAppointmentStore(code);
    }, [
        getBookAppointmentStore,
        store
    ]);
    React.useEffect(()=>{
        const { called , data  } = getBookAppointmentStoreQuery;
        if (!called || !data) {
            return;
        }
        setStore(data);
    }, [
        setStore,
        getBookAppointmentStoreQuery
    ]);
    const availableAgeGroups = useGetAvailableAgeGroups(sortedAppointmentTypes);
    const { AgeGroupSelector , selectedAgeGroup  } = useGetAgeGroupsSelectorAndSelectedAgeGroup(availableAgeGroups);
    const appointmentTypes1 = useGetAppointmentTypesForSelectedAgeGroup(selectedAgeGroup, sortedAppointmentTypes);
    const loading = getBookAppointmentStoreQuery.loading;
    return /*#__PURE__*/ React.createElement(AppointmentTypes, {
        labels: labels,
        AgeGroupSelector: AgeGroupSelector,
        appointmentTypes: appointmentTypes1,
        appointmentTypeIcons: appointmentTypeIcons,
        appointmentTypeToolTips: appointmentTypeToolTips,
        appointmentGlobalLabels: appointmentGlobalLabels,
        activeToolTip: activeToolTip,
        onClick: onClick,
        onClickToolTip: onClickToolTip,
        onCloseToolTip: onCloseToolTip,
        loading: loading
    });
};
var AppointmentTypesContainer$1 = AppointmentTypesContainer;

export { AppointmentTypesContainer$1 as default };
//# sourceMappingURL=AppointmentTypesContainer.js.map
